import { useEffect, useState } from "react";
import { Plus, Trash2, Wallet, ListChecks, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, formatRupiah, formatTanggal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const emptyForm = { title: "", amount: "", due_date: "", target_class: "" };

export default function Bills() {
  const { user, schoolId, academicYear, setAcademicYear } = useAuth();
  const canEdit = ["super_admin", "bendahara"].includes(user.role);
  const [bills, setBills] = useState(null);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [classes, setClasses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // {bill, rows}
  const [deleting, setDeleting] = useState(null);
  const [payTarget, setPayTarget] = useState(null); // {bill, student, remaining}
  const [payMethod, setPayMethod] = useState("Tunai");
  const [payAmount, setPayAmount] = useState("");
  const [paying, setPaying] = useState(false);
  const [ayears, setAyears] = useState([]);

  useEffect(() => {
    api.get("/academic-years").then((r) => {
      setAyears(r.data.years || []);
      if (!academicYear && r.data.current) setAcademicYear(r.data.current);
    }).catch(() => {});
  }, [schoolId]);

  const load = () => {
    setNeedsSchool(false);
    api.get(`/bills${academicYear ? `?ayear=${encodeURIComponent(academicYear)}` : ""}`)
      .then((r) => setBills(r.data))
      .catch((e) => {
        if (e.response?.status === 400) { setNeedsSchool(true); setBills([]); }
        else { setBills([]); toast.error(apiError(e)); }
      });
    api.get("/students")
      .then((r) => setClasses([...new Set(r.data.map((s) => s.class_name))].sort()))
      .catch(() => {});
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) { setNeedsSchool(true); setBills([]); return; }
    load();
  }, [schoolId, academicYear]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/bills", {
        title: form.title,
        amount: Number(form.amount),
        due_date: form.due_date,
        target_class: form.target_class === "semua" ? "" : form.target_class,
      });
      toast.success("Tagihan berhasil dibuat");
      setDialogOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (bill) => {
    try {
      const r = await api.get(`/bills/${bill.id}/status`);
      setDetail(r.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    setPaying(true);
    try {
      await api.post(`/bills/${payTarget.bill.id}/pay`, { student_id: payTarget.student.id, amount: Number(payAmount) || undefined, method: payMethod });
      toast.success(`Pembayaran ${payTarget.student.name} tercatat`);
      setPayTarget(null);
      openDetail(payTarget.bill);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPaying(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/bills/${deleting.id}`);
      toast.success("Tagihan dihapus");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="bills-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bills-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Tagihan Lainnya</h1>
          <p className="mt-1 text-sm text-slate-500">Uang kegiatan, seragam, dan tagihan custom lainnya.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            try {
              const res = await api.get("/export/bills", { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement("a");
              a.href = url;
              a.download = "rekap_tagihan.xlsx";
              a.click();
              URL.revokeObjectURL(url);
              toast.success("Rekap tagihan berhasil diunduh");
            } catch { toast.error("Gagal mengunduh rekap tagihan"); }
          }} data-testid="bills-export-button" className="gap-2 rounded-md">
            <Download className="h-4 w-4" strokeWidth={1.5} /> Ekspor Excel
          </Button>
          <Select value={academicYear} onValueChange={setAcademicYear}>
            <SelectTrigger className="w-[140px]" data-testid="bills-ayear-select"><SelectValue placeholder="Tahun ajaran" /></SelectTrigger>
            <SelectContent>
              {(ayears.length ? ayears : academicYear ? [academicYear] : []).map((y) => (
                <SelectItem key={y} value={y} data-testid={`bills-ayear-option-${y.replace("/", "-")}`}>TA {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canEdit && (
            <Button onClick={() => setDialogOpen(true)} data-testid="add-bill-button" className="gap-2 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> Buat Tagihan
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="overflow-x-auto">
          <Table data-testid="bills-table">
            <TableHeader>
              <TableRow>
                <TableHead>Judul Tagihan</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead>Jatuh Tempo</TableHead>
                <TableHead>Target</TableHead>
                <TableHead className="text-right">Terbayar</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bills === null && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {bills !== null && bills.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Belum ada tagihan. Klik "Buat Tagihan" untuk memulai.</TableCell></TableRow>
              )}
              {(bills || []).map((b) => (
                <TableRow key={b.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`bill-row-${b.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{b.title}</TableCell>
                  <TableCell className="text-right text-sm font-medium text-slate-900">{formatRupiah(b.amount)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{b.due_date || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{b.target_class ? `Kelas ${b.target_class}` : "Semua siswa"}</TableCell>
                  <TableCell className="text-right">
                    <Badge className={`rounded-full ${b.paid_count >= b.target_count ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-50" : "bg-blue-50 text-blue-600 hover:bg-blue-50"}`} data-testid={`bill-progress-${b.id}`}>
                      {b.paid_count}/{b.target_count} lunas
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(b)} data-testid={`bill-detail-${b.id}`} className="gap-1.5 text-slate-600">
                        <ListChecks className="h-3.5 w-3.5" strokeWidth={1.5} /> Detail
                      </Button>
                      {canEdit && (
                        <Button variant="ghost" size="icon" onClick={() => setDeleting(b)} data-testid={`bill-delete-${b.id}`}>
                          <Trash2 className="h-4 w-4 text-rose-500" strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} data-testid="bill-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Buat Tagihan Baru</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="b-title">Judul Tagihan</Label>
              <Input id="b-title" data-testid="bill-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Contoh: Uang Kegiatan Pramuka" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-amount">Jumlah per Siswa (Rp)</Label>
              <Input id="b-amount" type="number" min="1" data-testid="bill-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required placeholder="Contoh: 75000" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="b-due">Jatuh Tempo (opsional)</Label>
              <Input id="b-due" type="date" data-testid="bill-due-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Target Siswa</Label>
              <Select value={form.target_class || "semua"} onValueChange={(v) => setForm({ ...form, target_class: v })}>
                <SelectTrigger data-testid="bill-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="semua" data-testid="bill-target-semua">Semua Siswa</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`bill-target-${c}`}>Kelas {c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="bill-form-cancel">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="bill-form-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {saving ? "Menyimpan..." : "Buat Tagihan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl" data-testid="bill-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{detail?.bill?.title} — {formatRupiah(detail?.bill?.amount)}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto">
            <Table data-testid="bill-status-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nama Siswa</TableHead>
                  <TableHead>Kelas</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead className="text-right">Aksi</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(detail?.rows || []).map((r) => (
                  <TableRow key={r.student.id} data-testid={`bill-status-row-${r.student.nisn}`}>
                    <TableCell className="text-sm font-medium text-slate-800">{r.student.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">{r.student.class_name}</TableCell>
                    <TableCell>
                      {r.status === "lunas" && (
                        <Badge className="rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-50">Lunas</Badge>
                      )}
                      {r.status === "sebagian" && (
                        <Badge className="rounded-full bg-amber-50 text-amber-600 hover:bg-amber-50" data-testid={`bill-status-sebagian-${r.student.nisn}`}>Sebagian {r.percent}%</Badge>
                      )}
                      {r.status === "belum" && (
                        <Badge className="rounded-full bg-rose-50 text-rose-600 hover:bg-rose-50">Belum</Badge>
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        {r.status !== "lunas" && (
                          <Button size="sm" onClick={() => { const sisa = detail.bill.amount - (r.total_paid || 0); setPayTarget({ bill: detail.bill, student: r.student, remaining: sisa }); setPayAmount(String(sisa)); setPayMethod("Tunai"); }} data-testid={`bill-pay-${r.student.nisn}`} className="gap-1.5 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
                            <Wallet className="h-3.5 w-3.5" strokeWidth={1.5} /> {r.status === "sebagian" ? "Cicilan" : "Bayar"}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent aria-describedby={undefined} data-testid="bill-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Catat Pembayaran Tagihan</DialogTitle>
          </DialogHeader>
          {payTarget && (
            <form onSubmit={handlePay} className="space-y-4">
              <div className="rounded-lg bg-slate-50 px-4 py-3">
                <p className="text-sm font-medium text-slate-800">{payTarget.student.name}</p>
                <p className="text-xs text-slate-500">{payTarget.bill.title} · Total {formatRupiah(payTarget.bill.amount)} · Sisa {formatRupiah(payTarget.remaining)}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bp-amount">Jumlah Pembayaran (Rp)</Label>
                <Input id="bp-amount" type="number" min="1" max={payTarget.remaining} data-testid="bill-pay-amount" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} required />
                <p className="text-xs text-slate-400">Bisa dicicil — isi lebih kecil dari sisa untuk pembayaran sebagian.</p>
              </div>
              <div className="space-y-2">
                <Label>Metode Pembayaran</Label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger data-testid="bill-pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Tunai" data-testid="bill-pay-tunai">Tunai</SelectItem>
                    <SelectItem value="Transfer" data-testid="bill-pay-transfer">Transfer Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPayTarget(null)} data-testid="bill-pay-cancel">Batal</Button>
                <Button type="submit" disabled={paying} data-testid="bill-pay-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                  {paying ? "Menyimpan..." : "Simpan Pembayaran"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Hapus Tagihan"
        description={`Yakin ingin menghapus tagihan "${deleting?.title}"? Riwayat pembayaran yang sudah masuk tetap tersimpan.`}
        onConfirm={handleDelete}
        testId="confirm-delete-bill"
      />
    </div>
  );
}
